/**
 * RentIncreaseActionModal — Demande de hausse / renouvellement de bail (Portail propriétaire).
 *
 * Batch #14.166 — Refonte :
 *   • Champs pour 2 locataires (nom + adresse + courriel)
 *   • Adresse de l'immeuble auto-pull depuis la fiche (lecture seule)
 *   • Bloc propriétaire auto-pull depuis le master client (lecture seule)
 *   • Soumission stockée pour la génération de la lettre TAL par le cabinet
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { TrendingUp, Loader2, Send, Lock, UserPlus, UserMinus, CheckCircle2, FileText } from "lucide-react";
import { BaseActionModal, FieldLabel, API, authH } from "./shared";

export function RentIncreaseActionModal({ apartments, buildings, selectedBuilding, selectedApt, onClose, onReload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bId, setBId] = useState(selectedBuilding?.id || "");
  const aptsOfB = apartments.filter((a) => a.property_id === bId);
  const [showTenant2, setShowTenant2] = useState(false);
  const [autoCtx, setAutoCtx] = useState({
    landlord_name: "",
    landlord_address: "",
    property_address: "",
    unit_label: "",
    current_rent: null,
  });
  const [form, setForm] = useState({
    unit_id: selectedApt?.id || "",
    // Locataire 1
    tenant_name: "",
    tenant_address: "",
    tenant_email: "",
    tenant_phone: "",
    // Locataire 2 (optionnel)
    tenant_2_name: "",
    tenant_2_address: "",
    tenant_2_email: "",
    tenant_2_phone: "",
    // Loyer / dates
    current_rent: "",
    new_rent: "",
    effective_date: "",
    lease_renewal_date: "",
    notes: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Sync avec sélecteur en haut de page
  useEffect(() => {
    if (selectedBuilding?.id && selectedBuilding.id !== bId) {
      setBId(selectedBuilding.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding?.id]);
  useEffect(() => {
    if (selectedApt?.id && selectedApt.id !== form.unit_id) {
      setForm((f) => ({ ...f, unit_id: selectedApt.id, current_rent: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApt?.id]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/rent-increase-requests`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  // Pré-charge contexte auto (proprio + logement + locataires opt-in SMS) à chaque changement d'unité
  const loadContext = useCallback(async (unit_id) => {
    try {
      const r = await axios.get(`${API}/portal/immeuble/rent-increase-context`, {
        headers: authH(),
        params: unit_id ? { unit_id } : {},
      });
      const ctx = r.data || {};
      setAutoCtx(ctx);
      // Pré-remplit current_rent depuis l'unité si vide
      if (ctx.current_rent && !form.current_rent) {
        upd("current_rent", String(ctx.current_rent));
      }
      // Batch #14.188 — Auto-fill locataires + phones depuis tenants opt-in
      const tenantsList = ctx.tenants || [];
      if (tenantsList.length > 0) {
        setForm((f) => {
          const next = { ...f };
          const t1 = tenantsList[0];
          // Locataire 1 : auto-fill seulement si champs vides
          if (!f.tenant_name && t1.name) next.tenant_name = t1.name;
          if (!f.tenant_email && t1.email) next.tenant_email = t1.email;
          if (!f.tenant_phone && t1.notify_by_sms && t1.phone) next.tenant_phone = t1.phone;
          if (!f.tenant_address && t1.previous_address) next.tenant_address = t1.previous_address;
          // Locataire 2 : si présent ET pas déjà rempli
          if (tenantsList.length >= 2) {
            const t2 = tenantsList[1];
            if (!f.tenant_2_name && t2.name) next.tenant_2_name = t2.name;
            if (!f.tenant_2_email && t2.email) next.tenant_2_email = t2.email;
            if (!f.tenant_2_phone && t2.notify_by_sms && t2.phone) next.tenant_2_phone = t2.phone;
          }
          return next;
        });
        if (tenantsList.length >= 2) setShowTenant2(true);
      }
    } catch (_e) { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadContext(form.unit_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.unit_id]);

  const submit = async () => {
    if (!form.unit_id) return toast.error("Choisir un logement");
    const cur = Number(form.current_rent || 0);
    const neu = Number(form.new_rent || 0);
    if (cur <= 0 || neu <= 0) return toast.error("Loyers requis");
    if (neu <= cur) return toast.error("Le nouveau loyer doit être supérieur");
    if (!form.tenant_name.trim()) return toast.error("Nom du locataire #1 requis");
    if (showTenant2 && !form.tenant_2_name.trim()) {
      return toast.error("Renseignez le 2e locataire ou retirez-le");
    }
    setBusy(true);
    try {
      const body = {
        unit_id: form.unit_id,
        tenant_name: form.tenant_name.trim(),
        tenant_address: form.tenant_address.trim(),
        tenant_email: form.tenant_email.trim().toLowerCase() || null,
        tenant_phone: form.tenant_phone.trim() || null,
        tenant_2_name: showTenant2 ? form.tenant_2_name.trim() : null,
        tenant_2_address: showTenant2 ? form.tenant_2_address.trim() : null,
        tenant_2_email: showTenant2 ? (form.tenant_2_email.trim().toLowerCase() || null) : null,
        tenant_2_phone: showTenant2 ? (form.tenant_2_phone.trim() || null) : null,
        // landlord_* laissés au backend (auto-rempli depuis master)
        current_rent: cur,
        new_rent: neu,
        effective_date: form.effective_date,
        lease_renewal_date: form.lease_renewal_date,
        notes: form.notes,
      };
      await axios.post(`${API}/portal/immeuble/rent-increase-requests`, body, { headers: authH() });
      toast.success("Demande envoyée à l'équipe");
      setForm((f) => ({
        ...f,
        tenant_name: "", tenant_address: "", tenant_email: "", tenant_phone: "",
        tenant_2_name: "", tenant_2_address: "", tenant_2_email: "", tenant_2_phone: "",
        new_rent: "", effective_date: "", notes: "",
      }));
      setShowTenant2(false);
      fetchList();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <BaseActionModal
      title="Renouvellements"
      subtitle="Hausse de loyer + génération de lettre TAL signée par votre équipe"
      tone="indigo"
      icon={TrendingUp}
      testId="immeuble-action-modal-rent-increase"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="immeuble-rent-increase-close">Fermer</button>
          <button
            onClick={submit}
            disabled={busy || !form.unit_id || !form.new_rent || !form.tenant_name}
            className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-ri-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer au cabinet
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-3 text-[11px] text-indigo-900">
          <b>Workflow :</b> Vous saisissez la hausse → notre équipe rédige la lettre conforme TAL (art. 1942 Code civil) → envoi pour signature aux 2 locataires (s'il y en a 2) → vous accusez réception → archivage automatique.
        </div>

        {/* Sélecteur immeuble + logement */}
        <div className="grid grid-cols-2 gap-3">
          <FieldLabel label="Immeuble" required>
            <select value={bId} onChange={(e) => { setBId(e.target.value); upd("unit_id", ""); }} className="cc-input" data-testid="immeuble-ri-building">
              <option value="">— Choisir —</option>
              {buildings.map((b) => <option key={b.id} value={b.id}>{b.label || b.address || b.id}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Logement" required>
            <select value={form.unit_id} onChange={(e) => upd("unit_id", e.target.value)} className="cc-input" data-testid="immeuble-ri-unit">
              <option value="">— Choisir —</option>
              {aptsOfB.map((a) => <option key={a.id} value={a.id}>{a.label || a._raw?.unit_label || a.id}</option>)}
            </select>
          </FieldLabel>
        </div>

        {/* Adresse immeuble (auto-pull, lecture seule) */}
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="immeuble-ri-property-addr-block">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 inline-flex items-center gap-1">
            <Lock className="w-3 h-3" /> Adresse du logement (auto)
          </div>
          {autoCtx.property_address ? (
            <div className="text-xs text-slate-800 whitespace-pre-line" data-testid="immeuble-ri-property-addr">
              {autoCtx.property_address}{autoCtx.unit_label ? ` — Unité ${autoCtx.unit_label}` : ""}
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic" data-testid="immeuble-ri-property-addr">
              Sélectionnez d'abord un logement pour afficher l'adresse.
            </div>
          )}
        </div>

        {/* Propriétaire (auto-pull, lecture seule) */}
        {(autoCtx.landlord_name || autoCtx.landlord_address) && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="immeuble-ri-landlord-block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Propriétaire (auto)
            </div>
            <div className="text-xs text-slate-800" data-testid="immeuble-ri-landlord-name">
              <b>{autoCtx.landlord_name || "—"}</b>
            </div>
            {autoCtx.landlord_address && (
              <div className="text-[11px] text-slate-600 whitespace-pre-line mt-0.5">
                {autoCtx.landlord_address}
              </div>
            )}
          </div>
        )}

        {/* Locataire #1 */}
        <div className="border border-indigo-200 rounded-lg p-3 bg-white">
          <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-2">
            Locataire #1
          </div>
          <div className="space-y-2">
            <FieldLabel label="Nom complet" required>
              <input value={form.tenant_name} onChange={(e) => upd("tenant_name", e.target.value)} className="cc-input" data-testid="immeuble-ri-tenant1-name" />
            </FieldLabel>
            <FieldLabel label="Adresse (rue, ville, code postal)">
              <textarea rows={2} value={form.tenant_address} onChange={(e) => upd("tenant_address", e.target.value)} className="cc-input" data-testid="immeuble-ri-tenant1-address" />
            </FieldLabel>
            <FieldLabel label="Courriel (pour signature électronique)">
              <input type="email" value={form.tenant_email} onChange={(e) => upd("tenant_email", e.target.value)} className="cc-input" placeholder="locataire1@example.com" data-testid="immeuble-ri-tenant1-email" />
            </FieldLabel>
            <FieldLabel label="Téléphone mobile (optionnel — active SMS)">
              <input type="tel" value={form.tenant_phone} onChange={(e) => upd("tenant_phone", e.target.value)} className="cc-input" placeholder="+1 418 555-1234" data-testid="immeuble-ri-tenant1-phone" />
            </FieldLabel>
          </div>
        </div>

        {/* Locataire #2 (toggle) */}
        {!showTenant2 ? (
          <button
            type="button"
            onClick={() => setShowTenant2(true)}
            className="w-full text-xs px-3 py-2 rounded-lg border-2 border-dashed border-indigo-300 hover:bg-indigo-50 text-indigo-700 font-semibold inline-flex items-center justify-center gap-1.5"
            data-testid="immeuble-ri-add-tenant2"
          >
            <UserPlus className="w-3.5 h-3.5" /> Ajouter un 2e locataire (co-bail)
          </button>
        ) : (
          <div className="border border-indigo-200 rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">
                Locataire #2
              </div>
              <button
                type="button"
                onClick={() => { setShowTenant2(false); upd("tenant_2_name", ""); upd("tenant_2_address", ""); upd("tenant_2_email", ""); upd("tenant_2_phone", ""); }}
                className="text-[10px] text-rose-600 hover:underline inline-flex items-center gap-0.5"
                data-testid="immeuble-ri-remove-tenant2"
              >
                <UserMinus className="w-3 h-3" /> Retirer
              </button>
            </div>
            <div className="space-y-2">
              <FieldLabel label="Nom complet" required>
                <input value={form.tenant_2_name} onChange={(e) => upd("tenant_2_name", e.target.value)} className="cc-input" data-testid="immeuble-ri-tenant2-name" />
              </FieldLabel>
              <FieldLabel label="Adresse (si différente)">
                <textarea rows={2} value={form.tenant_2_address} onChange={(e) => upd("tenant_2_address", e.target.value)} className="cc-input" placeholder="(laisser vide si même adresse)" data-testid="immeuble-ri-tenant2-address" />
              </FieldLabel>
              <FieldLabel label="Courriel (pour signature électronique)">
                <input type="email" value={form.tenant_2_email} onChange={(e) => upd("tenant_2_email", e.target.value)} className="cc-input" placeholder="locataire2@example.com" data-testid="immeuble-ri-tenant2-email" />
              </FieldLabel>
              <FieldLabel label="Téléphone mobile (optionnel — active SMS)">
                <input type="tel" value={form.tenant_2_phone} onChange={(e) => upd("tenant_2_phone", e.target.value)} className="cc-input" placeholder="+1 418 555-1234" data-testid="immeuble-ri-tenant2-phone" />
              </FieldLabel>
            </div>
          </div>
        )}

        {/* Loyer + dates */}
        <div className="grid grid-cols-2 gap-3">
          <FieldLabel label="Loyer actuel ($)" required>
            <input type="number" step="0.01" value={form.current_rent} onChange={(e) => upd("current_rent", e.target.value)} className="cc-input font-mono" data-testid="immeuble-ri-current" />
          </FieldLabel>
          <FieldLabel label="Nouveau loyer ($)" required>
            <input type="number" step="0.01" value={form.new_rent} onChange={(e) => upd("new_rent", e.target.value)} className="cc-input font-mono" data-testid="immeuble-ri-new" />
          </FieldLabel>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FieldLabel label="Date d'entrée en vigueur">
            <input type="date" value={form.effective_date} onChange={(e) => upd("effective_date", e.target.value)} className="cc-input" data-testid="immeuble-ri-effective" />
          </FieldLabel>
          <FieldLabel label="Date renouvellement bail">
            <input type="date" value={form.lease_renewal_date} onChange={(e) => upd("lease_renewal_date", e.target.value)} className="cc-input" data-testid="immeuble-ri-renewal" />
          </FieldLabel>
        </div>

        <FieldLabel label="Notes (optionnel)">
          <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={500} className="cc-input" data-testid="immeuble-ri-notes" />
        </FieldLabel>

        {items.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Demandes en cours ({items.length})
            </div>
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /> : (
              <ul className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="immeuble-ri-list">
                {items.slice(0, 8).map((r) => (
                  <RentIncreaseRow key={r.id} item={r} onAck={async () => {
                    if (!window.confirm(
                      "Confirmer l'accusé de réception ? Cela finalisera l'avis et déclenchera l'envoi du PDF final par courriel (avec copie à votre équipe DG)."
                    )) return;
                    try {
                      await axios.post(
                        `${API}/portal/immeuble/rent-increase-requests/${r.id}/acknowledge`,
                        {},
                        { headers: authH() },
                      );
                      toast.success("Accusé de réception enregistré");
                      fetchList();
                      onReload?.();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Erreur");
                    }
                  }} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </BaseActionModal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Batch #14.215 — Ligne de demande avec affichage réponses + bouton Accuser
// ════════════════════════════════════════════════════════════════════════════
function RentIncreaseRow({ item: r, onAck }) {
  const t1 = r.tenant_response;
  const t2 = r.tenant_2_response;
  const sigStatus = r.signature_status || r.status;
  const decisionBadge = (resp, label) => {
    if (!resp) return null;
    const d = resp.decision;
    const cls = d === "accepte"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-rose-100 text-rose-700";
    const icon = d === "accepte" ? "✓" : "✗";
    const txt = d === "accepte" ? "ACCEPTÉ" : "REFUSÉ";
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${cls}`} data-testid={`immeuble-ri-${label}-decision-${r.id}`}>
        {icon} {label} : {txt}
      </span>
    );
  };

  const downloadSignedPdf = async () => {
    try {
      const resp = await axios.get(
        `${API}/portal/immeuble/rent-increase-requests/${r.id}/signed-pdf`,
        { headers: authH(), responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Avis-renouvellement-signe-${r.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de télécharger le PDF");
    }
  };

  return (
    <li className="text-[11px] p-2.5 rounded-lg bg-slate-50 border border-slate-200 space-y-1.5">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 truncate">
            {Number(r.current_rent).toFixed(0)}$ → {Number(r.new_rent).toFixed(0)}$
          </div>
          <div className="text-[10px] text-slate-500">
            {r.tenant_name || "—"}
            {r.tenant_2_name ? ` + ${r.tenant_2_name}` : ""}
            {" · "}{r.effective_date || "?"}
          </div>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
          sigStatus === "completed" || sigStatus === "signature_completed" ? "bg-emerald-100 text-emerald-700" :
          sigStatus === "pending_landlord" ? "bg-violet-100 text-violet-700" :
          sigStatus === "letter_ready" ? "bg-emerald-100 text-emerald-700" :
          "bg-amber-100 text-amber-700"
        }`}>
          {sigStatus === "completed" || sigStatus === "signature_completed" ? "Signé" :
           sigStatus === "pending_landlord" ? "À accuser" :
           sigStatus === "letter_ready" ? "Lettre prête" :
           (sigStatus || "").startsWith("signature_pending") || (sigStatus || "").startsWith("pending_tenant") ? "Signature en cours" :
           "En attente"}
        </span>
      </div>

      {/* Affichage des réponses locataires */}
      {(t1 || t2) && (
        <div className="flex flex-wrap gap-1.5">
          {decisionBadge(t1, "Loc1")}
          {decisionBadge(t2, "Loc2")}
        </div>
      )}

      {/* Bouton Accuser de réception — visible uniquement si en attente */}
      {sigStatus === "pending_landlord" && (
        <button
          onClick={onAck}
          className="w-full mt-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5"
          data-testid={`immeuble-ri-acknowledge-${r.id}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Accuser de réception
        </button>
      )}

      {/* Bouton télécharger PDF signé — visible une fois complété */}
      {(sigStatus === "completed" || sigStatus === "signature_completed") && (
        <button
          onClick={downloadSignedPdf}
          className="w-full mt-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5"
          data-testid={`immeuble-ri-download-signed-${r.id}`}
        >
          <FileText className="w-3.5 h-3.5" /> Télécharger le PDF signé
        </button>
      )}
    </li>
  );
}
